/**
 * Usage Warning System - CRITICAL IMPLEMENTATION
 * Users MUST be alerted at 70%, 90%, and 100% usage thresholds
 */

import {
  UsageWarningLevel,
  UsageWarningMessage,
  ActiveUsageWarning,
  UsageWarningEventPayload,
} from '../types/billing.types';

// ============================================================================
// WARNING THRESHOLDS & MESSAGES
// ============================================================================

/**
 * MANDATORY WARNING THRESHOLDS
 * These cannot be changed without affecting compliance
 */
export const USAGE_WARNING_THRESHOLDS = [70, 90, 100] as const;

/**
 * WARNING MESSAGES - Localized copy for each threshold
 * Level: 70% = warning, 90% = critical, 100% = exhausted
 */
export const USAGE_WARNING_MESSAGES: Record<number, UsageWarningMessage> = {
  70: {
    threshold: 70,
    level: UsageWarningLevel.WARNING,
    messageCopy:
      "You've used 70% of your monthly credits. Consider a top-up to avoid interruption.",
    actionRequired: false,
    suggestedAction: 'View top-up options',
  },
  90: {
    threshold: 90,
    level: UsageWarningLevel.CRITICAL,
    messageCopy:
      'Critical: 90% of your credits consumed. Service may be limited soon.',
    actionRequired: true,
    suggestedAction: 'Purchase top-up immediately',
  },
  100: {
    threshold: 100,
    level: UsageWarningLevel.CRITICAL,
    messageCopy:
      'Usage exhausted. Purchase a top-up to continue using ARCaml services.',
    actionRequired: true,
    suggestedAction: 'Purchase top-up now',
  },
};

// ============================================================================
// WARNING DETECTION & GENERATION
// ============================================================================

/**
 * Detect which warning threshold user has crossed
 * @param percentageUsed Current usage percentage
 * @param previousPercentage Previous usage percentage (for detecting NEW warnings)
 * @returns Threshold that was crossed, or null if no threshold reached
 */
export function detectWarningThreshold(
  percentageUsed: number,
  previousPercentage: number = 0
): number | null {
  // Check in order: 100%, 90%, 70%
  const thresholds = [...USAGE_WARNING_THRESHOLDS].reverse();
  for (const threshold of thresholds) {
    if (percentageUsed >= threshold && previousPercentage < threshold) {
      return threshold;
    }
  }
  return null;
}

/**
 * Generate active warning object when threshold is crossed
 */
export function generateUsageWarning(
  threshold: number,
  percentageUsed: number,
  creditsRemaining: number,
  subscriptionId: string
): ActiveUsageWarning {
  const messageData = USAGE_WARNING_MESSAGES[threshold];
  if (!messageData) {
    throw new Error(`Invalid threshold: ${threshold}`);
  }

  const warningId = `warning-${subscriptionId}-${threshold}-${Date.now()}`;

  return {
    warningId,
    threshold,
    level: messageData.level,
    message: messageData.messageCopy,
    triggeredAt: new Date().toISOString(),
    isDismissed: false,
    actionUrl: messageData.actionRequired ? '/billing/top-up' : undefined,
    actionLabel: messageData.suggestedAction,
  };
}

/**
 * Evaluate current usage and return array of active warnings
 * @param percentageUsed Current usage percentage (0-100)
 * @param creditsRemaining Credits left in billing period
 * @param subscriptionId Subscription ID for tracking
 * @returns Array of active warnings (may be empty)
 */
export function evaluateUsageWarnings(
  percentageUsed: number,
  creditsRemaining: number,
  subscriptionId: string
): ActiveUsageWarning[] {
  const warnings: ActiveUsageWarning[] = [];

  // If at/above 100%, return 100% warning (exhausted)
  if (percentageUsed >= 100) {
    warnings.push(
      generateUsageWarning(100, percentageUsed, creditsRemaining, subscriptionId)
    );
    return warnings;
  }

  // If at/above 90%, return 90% warning (critical)
  if (percentageUsed >= 90) {
    warnings.push(
      generateUsageWarning(90, percentageUsed, creditsRemaining, subscriptionId)
    );
    return warnings;
  }

  // If at/above 70%, return 70% warning
  if (percentageUsed >= 70) {
    warnings.push(
      generateUsageWarning(70, percentageUsed, creditsRemaining, subscriptionId)
    );
    return warnings;
  }

  // No warning for <70% usage
  return warnings;
}

// ============================================================================
// WARNING DISPLAY & UX HOOKS
// ============================================================================

/**
 * Get CSS class for warning severity
 */
export function getWarningClassName(level: UsageWarningLevel): string {
  const baseClass = 'usage-warning';
  switch (level) {
    case UsageWarningLevel.CRITICAL:
      return `${baseClass} ${baseClass}--critical`;
    case UsageWarningLevel.WARNING:
      return `${baseClass} ${baseClass}--warning`;
    default:
      return baseClass;
  }
}

/**
 * Get icon for warning level
 */
export function getWarningIcon(level: UsageWarningLevel): string {
  switch (level) {
    case UsageWarningLevel.CRITICAL:
      return '🔴'; // Critical - red circle
    case UsageWarningLevel.WARNING:
      return '⏱️'; // Warning - timer icon for usage
    default:
      return '';
  }
}

/**
 * Determine if warning should block user actions
 */
export function shouldBlockActions(level: UsageWarningLevel): boolean {
  return level === UsageWarningLevel.CRITICAL;
}

/**
 * Format warning message with dynamic data
 */
export function formatWarningMessage(
  template: string,
  data: {
    percentageUsed?: number;
    creditsRemaining?: number;
    threshold?: number;
  }
): string {
  let message = template;
  if (data.percentageUsed !== undefined) {
    message = message.replace('{percentageUsed}', String(data.percentageUsed));
  }
  if (data.creditsRemaining !== undefined) {
    message = message.replace('{creditsRemaining}', String(data.creditsRemaining));
  }
  if (data.threshold !== undefined) {
    message = message.replace('{threshold}', String(data.threshold));
  }
  return message;
}

// ============================================================================
// ANALYTICS & TRACKING
// ============================================================================

/**
 * Log usage warning event for analytics
 */
export function logUsageWarningEvent(
  payload: UsageWarningEventPayload
): void {
  // This would integrate with your analytics service

  // Send to analytics backend
  const gtag = (window as any).gtag;
  if (gtag) {
    gtag('event', 'usage_warning', {
      warning_id: payload.warningId,
      threshold: payload.threshold,
      percentage_used: payload.percentageUsed,
      credits_remaining: payload.creditsRemaining,
      timestamp: payload.timestamp,
      user_action: payload.userAction,
    });
  }
}

/**
 * Track when user dismisses warning
 */
export function trackWarningDismissal(warningId: string): void {
  logUsageWarningEvent({
    warningId,
    threshold: 0,
    percentageUsed: 0,
    creditsRemaining: 0,
    timestamp: new Date().toISOString(),
    userAction: 'dismissed',
  });
}

/**
 * Track when user acts on warning (e.g., purchases top-up)
 */
export function trackWarningAction(
  warningId: string,
  actionType: 'viewed' | 'acted_on'
): void {
  logUsageWarningEvent({
    warningId,
    threshold: 0,
    percentageUsed: 0,
    creditsRemaining: 0,
    timestamp: new Date().toISOString(),
    userAction: actionType,
  });
}

// ============================================================================
// LOCAL STORAGE PERSISTENCE
// ============================================================================

/**
 * Save dismissed warnings to prevent repeat alerts
 */
export function saveDismissedWarning(warningId: string): void {
  const dismissed = JSON.parse(
    localStorage.getItem('dismissedUsageWarnings') || '[]'
  );
  if (!dismissed.includes(warningId)) {
    dismissed.push(warningId);
    localStorage.setItem('dismissedUsageWarnings', JSON.stringify(dismissed));
  }
}

/**
 * Check if warning was already dismissed
 */
export function isWarningDismissed(warningId: string): boolean {
  const dismissed = JSON.parse(
    localStorage.getItem('dismissedUsageWarnings') || '[]'
  );
  return dismissed.includes(warningId);
}

/**
 * Clear all dismissed warnings (useful for testing or reset)
 */
export function clearDismissedWarnings(): void {
  localStorage.removeItem('dismissedUsageWarnings');
}

/**
 * Get timestamp of last warning alert
 */
export function getLastWarningTimestamp(): string | null {
  return localStorage.getItem('lastUsageWarningTime');
}

/**
 * Update last warning timestamp
 */
export function updateLastWarningTimestamp(): void {
  localStorage.setItem('lastUsageWarningTime', new Date().toISOString());
}
